import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";

type ThemeMode = "light" | "dark";
type ThemeContextType = {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

/**
 * CSP `sandbox` without `allow-same-origin` (share CDN for canvas-view)
 * throws on any access to `window.localStorage`, including reading the
 * property itself — not only getItem/setItem.
 */
let localStorageAvailableCache: boolean | null = null;

function isLocalStorageAvailable(): boolean {
  if (localStorageAvailableCache != null) return localStorageAvailableCache;
  if (typeof window === "undefined") {
    localStorageAvailableCache = false;
    return false;
  }
  try {
    const key = "__theme_storage_test__";
    window.localStorage.setItem(key, "1");
    window.localStorage.removeItem(key);
    localStorageAvailableCache = true;
  } catch {
    localStorageAvailableCache = false;
  }
  return localStorageAvailableCache;
}

function readStoredTheme(): ThemeMode | null {
  if (!isLocalStorageAvailable()) return null;
  try {
    const saved = window.localStorage.getItem("theme");
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // ignore
  }
  return null;
}

function writeStoredTheme(mode: ThemeMode): void {
  if (!isLocalStorageAvailable()) return;
  try {
    window.localStorage.setItem("theme", mode);
  } catch {
    // ignore
  }
}

const getInitialTheme = (): ThemeMode => {
  if (typeof window === "undefined") {
    return "light";
  }

  const savedTheme = readStoredTheme();
  if (savedTheme) return savedTheme;

  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  return "light";
};

const initialTheme = getInitialTheme();
const localStorageAvailable = isLocalStorageAvailable();

if (typeof document !== "undefined") {
  if (initialTheme === "dark") {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

export const ThemeProvider = ({
  children,
  theme,
}: PropsWithChildren<{ theme?: ThemeMode }>) => {
  const [mode, setMode] = useState<ThemeMode>(theme || initialTheme);

  useEffect(() => {
    if (theme) {
      setMode(theme);
    }
  }, [theme]);

  useEffect(() => {
    // Without durable storage, OS preference changes would fight in-session toggles.
    if (!localStorageAvailable) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (readStoredTheme() == null) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (mode === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    writeStoredTheme(mode);
  }, [mode]);

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
